import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTeacher } from '@/context/TeacherContext';
import { useToast } from '@/hooks/use-toast';
import {
  ArrowLeft,
  Search,
  Sparkles,
  Users,
  Clock,
  TrendingUp,
  BookOpen,
  Video,
  Headphones,
  Wrench,
  FileText,
  BarChart3,
  Download,
  Eye,
  ChevronDown,
  ChevronUp,
  Zap,
  Lightbulb,
  Target,
  Award
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn, getButtonColor, getBadgeColor } from '@/lib/utils';
import { EmptyState } from '@/components/ui/EmptyState';

// Helper function to extract grade and division from class name
const getGradeAndDivision = (className) => {
  if (!className) return '';
  // Extract "Grade XA" or "Grade XB" pattern from strings like "Physics - Grade 8A"
  const match = className.match(/Grade\s+(\d+[A-Z])/i);
  if (match) {
    return `Grade ${match[1]}`;
  }
  // Fallback: if no "Grade" prefix, try to extract just the grade and division
  const fallbackMatch = className.match(/(\d+[A-Z])/);
  if (fallbackMatch) {
    return `Grade ${fallbackMatch[1]}`;
  }
  return className; // Return original if no pattern matches
};

// Minimal Student Data: 4 students total (1 high, 1 medium, 2 low usage)
const studentGroups = {
  'Physics - Grade 7A': [
    { id: 1, name: 'Anjali Patel', class: 'Physics - Grade 7A', avgScore: 88 }, // High usage
    { id: 2, name: 'Arjun Sharma', class: 'Physics - Grade 7A', avgScore: 45 }  // Medium usage
  ],
  'Physics - Grade 8A': [
    { id: 4, name: 'Shreya Patel', class: 'Physics - Grade 8A', avgScore: 48 },    // Low usage
    { id: 6, name: 'Siddharth Joshi', class: 'Physics - Grade 8A', avgScore: 42 } // Low usage
  ]
};

// Minimal Curiosity Analytics data mapped to student IDs (4 students total)
const curiosityAnalyticsData = {
  1: { totalInteractions: 55, timeSpentMinutes: 150, coreInteractions: 32, deepInteractions: 23, completionRate: 91.2, chaptersEngaged: 11, usageLevel: 'high' },   // Anjali - High
  2: { totalInteractions: 22, timeSpentMinutes: 55, coreInteractions: 18, deepInteractions: 4, completionRate: 65.2, chaptersEngaged: 5, usageLevel: 'medium' },  // Arjun - Medium
  4: { totalInteractions: 16, timeSpentMinutes: 40, coreInteractions: 12, deepInteractions: 4, completionRate: 59.2, chaptersEngaged: 3, usageLevel: 'low' },   // Shreya - Low
  6: { totalInteractions: 12, timeSpentMinutes: 30, coreInteractions: 10, deepInteractions: 2, completionRate: 54.8, chaptersEngaged: 2, usageLevel: 'low' }   // Siddharth - Low
};

// Helper function to get all students with curiosity analytics
const getAllStudentsWithCuriosity = (currentClass) => {
  let students = [];
  if (currentClass) {
    const className = currentClass.fullName || currentClass.name;
    students = studentGroups[className] || [];
  } else {
    students = Object.values(studentGroups).flat();
  }
  
  // Merge with curiosity analytics data
  return students.map(student => ({
    ...student,
    ...(curiosityAnalyticsData[student.id] || {
      totalInteractions: 0,
      timeSpentMinutes: 0,
      coreInteractions: 0,
      deepInteractions: 0,
      completionRate: 0,
      chaptersEngaged: 0,
      usageLevel: 'low'
    }),
    recentActivity: [
      { date: '2024-01-15', content: 'Light Reflection Video', type: 'core', contentType: 'video' }
    ],
    chapterBreakdown: [
      { chapter: 'Light and Reflection', interactions: Math.floor((curiosityAnalyticsData[student.id]?.totalInteractions || 0) / 2), core: Math.floor((curiosityAnalyticsData[student.id]?.coreInteractions || 0) / 2), deep: Math.floor((curiosityAnalyticsData[student.id]?.deepInteractions || 0) / 2) }
    ]
  }));
};

const MOCK_CHAPTERS = [
  {
    id: 1,
    name: 'Light and Reflection',
    subject: 'Physics',
    totalInteractions: 105,
    uniqueStudents: 4,
    coreUsage: 72,
    deepUsage: 33,
    avgCompletionRate: 72.3,
    topContent: [
      { title: 'Light Reflection Video', interactions: 45, type: 'core', contentType: 'video' },
      { title: 'Advanced Optics Simulation', interactions: 20, type: 'deep', contentType: 'simulation' }
    ]
  },
  {
    id: 2,
    name: 'Motion and Forces',
    subject: 'Physics',
    totalInteractions: 50,
    uniqueStudents: 3,
    coreUsage: 40,
    deepUsage: 10,
    avgCompletionRate: 68.5,
    topContent: [
      { title: 'Newton\'s Laws Explained', interactions: 38, type: 'core', contentType: 'video' }
    ]
  },
  {
    id: 3,
    name: 'Sound Waves',
    subject: 'Physics',
    totalInteractions: 30,
    uniqueStudents: 2,
    coreUsage: 22,
    deepUsage: 8,
    avgCompletionRate: 70.1,
    topContent: [
      { title: 'Sound Wave Basics', interactions: 42, type: 'core', contentType: 'video' }
    ]
  }
];

const MOCK_CONTENT = [
  {
    id: 1,
    title: 'Light Reflection Video',
    type: 'video',
    category: 'core',
    totalViews: 145,
    completionRate: 78.5,
    avgTimeSpent: 12.5,
    chapter: 'Light and Reflection',
    subject: 'Physics'
  },
  {
    id: 2,
    title: 'Advanced Optics Simulation',
    type: 'simulation',
    category: 'deep',
    totalViews: 68,
    completionRate: 85.2,
    avgTimeSpent: 18.3,
    chapter: 'Light and Reflection',
    subject: 'Physics'
  },
  {
    id: 3,
    title: 'Newton\'s Laws Explained',
    type: 'video',
    category: 'core',
    totalViews: 132,
    completionRate: 72.1,
    avgTimeSpent: 15.2,
    chapter: 'Motion and Forces',
    subject: 'Physics'
  },
  {
    id: 4,
    title: 'Sound Wave Basics',
    type: 'video',
    category: 'core',
    totalViews: 128,
    completionRate: 75.3,
    avgTimeSpent: 14.8,
    chapter: 'Sound Waves',
    subject: 'Physics'
  }
];

// Get available classes from studentGroups
const MOCK_CLASSES = Object.keys(studentGroups);

const MOCK_SUBJECTS = [
  'Physics',
  'Mathematics',
  'Chemistry',
  'Biology',
];

export default function TeacherCuriosityAnalytics() {
  const navigate = useNavigate();
  const { teacherTheme, classes, currentClass } = useTeacher();
  const { toast } = useToast();
  const isDark = teacherTheme === 'dark';

  // State Management - Simplified
  const [selectedClass, setSelectedClass] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('interactions');
  const [sortOrder, setSortOrder] = useState('desc');

  // Get all students with curiosity analytics (synced with Student Insights)
  const allStudentsWithCuriosity = useMemo(() => {
    return getAllStudentsWithCuriosity(currentClass);
  }, [currentClass]);

  // Simplified Filtering Logic - Only class and search
  const filteredStudents = useMemo(() => {
    return allStudentsWithCuriosity.filter(student => {
      const matchesClass = selectedClass === 'all' || student.class === selectedClass;
      const matchesSearch = searchQuery === '' || 
        student.name.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesClass && matchesSearch;
    });
  }, [allStudentsWithCuriosity, selectedClass, searchQuery]);

  // Sorting Logic
  const sortedStudents = useMemo(() => {
    return [...filteredStudents].sort((a, b) => {
      if (sortBy === 'name') {
        return sortOrder === 'asc'
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      } else if (sortBy === 'interactions') {
        const aVal = a.totalInteractions || 0;
        const bVal = b.totalInteractions || 0;
        return sortOrder === 'asc' 
          ? aVal - bVal
          : bVal - aVal;
      } else if (sortBy === 'time') {
        const aVal = a.timeSpentMinutes || 0;
        const bVal = b.timeSpentMinutes || 0;
        return sortOrder === 'asc'
          ? aVal - bVal
          : bVal - aVal;
      } else if (sortBy === 'completion') {
        const aVal = a.completionRate || 0;
        const bVal = b.completionRate || 0;
        return sortOrder === 'asc'
          ? aVal - bVal
          : bVal - aVal;
      }
      return 0;
    });
  }, [filteredStudents, sortBy, sortOrder]);

  // Overview Stats Calculation
  const overviewStats = useMemo(() => {
    const totalInteractions = filteredStudents.reduce((sum, s) => sum + (s.totalInteractions || 0), 0);
    const activeStudents = filteredStudents.length;
    const avgTimeSpent = filteredStudents.length > 0
      ? Math.round(filteredStudents.reduce((sum, s) => sum + (s.timeSpentMinutes || 0), 0) / filteredStudents.length)
      : 0;
    const totalCore = filteredStudents.reduce((sum, s) => sum + (s.coreInteractions || 0), 0);
    const totalDeep = filteredStudents.reduce((sum, s) => sum + (s.deepInteractions || 0), 0);
    const totalAll = totalCore + totalDeep;
    const corePercentage = totalAll > 0 ? Math.round((totalCore / totalAll) * 100) : 0;
    const deepPercentage = totalAll > 0 ? Math.round((totalDeep / totalAll) * 100) : 0;

    return {
      totalInteractions,
      activeStudents,
      avgTimeSpent,
      corePercentage,
      deepPercentage
    };
  }, [filteredStudents]);

  // Simplified Core vs Deep Stats
  const coreVsDeepStats = useMemo(() => {
    const totalCore = filteredStudents.reduce((sum, s) => sum + (s.coreInteractions || 0), 0);
    const totalDeep = filteredStudents.reduce((sum, s) => sum + (s.deepInteractions || 0), 0);
    return { totalCore, totalDeep };
  }, [filteredStudents]);

  // Handlers
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const handleExportReport = () => {
    const className = currentClass ? (currentClass.fullName || currentClass.name) : 'All Classes';
    const reportDate = new Date().toLocaleDateString();
    
    let reportContent = `Curiosity Corner Analytics Report\n${'='.repeat(50)}\n\n`;
    reportContent += `Generated on: ${reportDate}\n`;
    reportContent += `Class: ${className}\n`;
    reportContent += `Total Students: ${overviewStats.activeStudents}\n\n`;
    reportContent += `${'='.repeat(50)}\n`;
    reportContent += `OVERVIEW STATISTICS\n`;
    reportContent += `${'='.repeat(50)}\n\n`;
    reportContent += `Total Interactions: ${overviewStats.totalInteractions}\n`;
    reportContent += `Active Students: ${overviewStats.activeStudents}\n`;
    reportContent += `Average Time Spent: ${overviewStats.avgTimeSpent} minutes\n`;
    reportContent += `Core vs Deep Ratio: ${overviewStats.corePercentage}% Core / ${overviewStats.deepPercentage}% Deep\n\n`;
    
    reportContent += `${'='.repeat(50)}\n`;
    reportContent += `STUDENT DETAILS\n`;
    reportContent += `${'='.repeat(50)}\n\n`;
    
    if (sortedStudents.length === 0) {
      reportContent += `No students found matching the current filters.\n`;
    } else {
      sortedStudents.forEach((student, index) => {
        reportContent += `${index + 1}. ${student.name}\n`;
        reportContent += `   Class: ${getGradeAndDivision(student.class)}\n`;
        reportContent += `   Interactions: ${student.totalInteractions || 0}\n`;
        reportContent += `   Time Spent: ${student.timeSpentMinutes || 0} minutes\n`;
        reportContent += `   Completion Rate: ${student.completionRate ? student.completionRate.toFixed(1) : '0.0'}%\n`;
        reportContent += `   Core/Deep: ${student.coreInteractions || 0}/${student.deepInteractions || 0}\n`;
        reportContent += `   Usage Level: ${(student.usageLevel || 'low').toUpperCase()}\n\n`;
      });
    }
    
    reportContent += `${'='.repeat(50)}\n`;
    reportContent += `END OF REPORT\n`;
    reportContent += `${'='.repeat(50)}\n\n`;
    reportContent += `Note: This is a mock report. Backend will integrate real data here.\n`;
    
    const blob = new Blob([reportContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `curiosity_analytics_report_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast({
      title: "Report Generated",
      description: "Curiosity analytics report downloaded successfully",
    });
  };

  const handleViewStudentDetails = (studentId) => {
    toast({
      title: "Opening Details",
      description: `Viewing detailed analytics for student`,
    });
    navigate(`/teacher/student/${studentId}`);
  };

  const handleViewChapterDetails = (chapterId) => {
    toast({
      title: "Chapter Details",
      description: `Viewing detailed analytics for chapter`,
    });
  };

  const getUsageLevelColor = (level) => {
    if (level === 'high') return isDark ? 'bg-[hsl(var(--gold-primary))]' : 'bg-[#C9A961]';
    if (level === 'medium') return isDark ? 'bg-[hsl(var(--gold-medium))]' : 'bg-[#D4AF77]';
    return isDark ? 'bg-[hsl(var(--card-border))]' : 'bg-[#8B7B6B]';
  };

  const getContentTypeIcon = (type) => {
    switch (type) {
      case 'video': return Video;
      case 'podcast': return Headphones;
      case 'simulation': return Wrench;
      case 'course': return BookOpen;
      case 'tool': return Zap;
      default: return FileText;
    }
  };

  return (
    <div className={cn("flex flex-col h-full p-6 overflow-y-auto", isDark ? "bg-gradient-to-br from-[hsl(var(--background))] via-[hsl(var(--card))] to-[hsl(var(--background))]" : "bg-gradient-to-br from-[#F8F5F0] via-[#F5F1EA] to-[#F2EBE0]")}>
      <div className="max-w-7xl mx-auto w-full space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/teacher/dashboard')}
              className={cn("rounded-xl", isDark ? "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--card-border))]/60" : "text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F0EBE3]/60")}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className={cn("text-4xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>Curiosity Corner Analytics</h1>
              <p className={cn("mt-1 text-base font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Track and analyze student curiosity engagement and beyond-the-book learning</p>
            </div>
          </div>
          <Button
            onClick={handleExportReport}
            className={cn("h-9 text-sm font-medium rounded-lg shadow-sm", getButtonColor(isDark))}
          >
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>

        {/* Simple Filters Row */}
        <Card className={cn("border shadow-sm", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-white border-[#E0D4BF]")}>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <Select value={selectedClass} onValueChange={setSelectedClass}>
                <SelectTrigger className={cn("w-full sm:w-[200px]", isDark ? "bg-[hsl(var(--background))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))]" : "bg-white border-[#E0D4BF] text-[#2B2B2B]")}>
                  <SelectValue placeholder="All Classes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Classes</SelectItem>
                  {MOCK_CLASSES.map(cls => (
                    <SelectItem key={cls} value={cls}>{cls}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="relative flex-1">
                <Search className={cn("absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")} />
                <Input
                  type="text"
                  placeholder="Search students..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={cn("pl-10 w-full", isDark ? "bg-[hsl(var(--background))] border-[hsl(var(--card-border))] text-[hsl(var(--foreground))]" : "bg-white border-[#E0D4BF] text-[#2B2B2B]")}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Simple Overview Stats - 3 Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className={cn("border shadow-sm", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-white border-[#E0D4BF]")}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className={cn("text-sm font-medium mb-1", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Total Interactions</p>
                  <p className={cn("text-3xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{overviewStats.totalInteractions}</p>
                </div>
                <div className={cn("p-3 rounded-lg", isDark ? "bg-[hsl(var(--gold-primary))]/20" : "bg-[#C9A961]/20")}>
                  <Sparkles className={cn("w-6 h-6", isDark ? "text-[hsl(var(--gold-primary))]" : "text-[#C9A961]")} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={cn("border shadow-sm", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-white border-[#E0D4BF]")}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className={cn("text-sm font-medium mb-1", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Active Students</p>
                  <p className={cn("text-3xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{overviewStats.activeStudents}</p>
                </div>
                <div className={cn("p-3 rounded-lg", isDark ? "bg-[hsl(var(--gold-primary))]/20" : "bg-[#C9A961]/20")}>
                  <Users className={cn("w-6 h-6", isDark ? "text-[hsl(var(--gold-primary))]" : "text-[#C9A961]")} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={cn("border shadow-sm", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-white border-[#E0D4BF]")}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className={cn("text-sm font-medium mb-1", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>Avg Time Spent</p>
                  <p className={cn("text-3xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{overviewStats.avgTimeSpent} min</p>
                </div>
                <div className={cn("p-3 rounded-lg", isDark ? "bg-[hsl(var(--gold-primary))]/20" : "bg-[#C9A961]/20")}>
                  <Clock className={cn("w-6 h-6", isDark ? "text-[hsl(var(--gold-primary))]" : "text-[#C9A961]")} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Simple Student List - No Tabs */}
        <Card className={cn("border shadow-sm", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-white border-[#E0D4BF]")}>
          <CardHeader>
            <CardTitle className={cn("text-2xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Student Curiosity Usage</CardTitle>
            <p className={cn("text-sm font-medium mt-1", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>View how much each student is using Curiosity Corner</p>
          </CardHeader>
          <CardContent>
            {sortedStudents.length === 0 ? (
              <EmptyState
                title="No students found"
                description="Try adjusting your filters"
              />
            ) : (
              <>
                {/* Simple Sortable Header */}
                <div className={cn("hidden md:grid grid-cols-12 gap-4 p-3 mb-3 rounded-lg border-b", isDark ? "bg-[hsl(var(--background))]/50 border-[hsl(var(--card-border))]" : "bg-[#F8F5F0] border-[#E0D4BF]")}>
                  <div className="col-span-4">
                    <button
                      onClick={() => handleSort('name')}
                      className={cn("flex items-center gap-2 font-bold text-sm hover:opacity-80", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}
                    >
                      Student Name
                      {sortBy === 'name' && (sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                    </button>
                  </div>
                  <div className="col-span-2 text-center">
                    <button
                      onClick={() => handleSort('interactions')}
                      className={cn("flex items-center justify-center gap-2 font-bold text-sm hover:opacity-80 mx-auto", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}
                    >
                      Interactions
                      {sortBy === 'interactions' && (sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                    </button>
                  </div>
                  <div className="col-span-2 text-center">
                    <button
                      onClick={() => handleSort('time')}
                      className={cn("flex items-center justify-center gap-2 font-bold text-sm hover:opacity-80 mx-auto", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}
                    >
                      Time Spent
                      {sortBy === 'time' && (sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                    </button>
                  </div>
                  <div className="col-span-2 text-center">
                    <button
                      onClick={() => handleSort('completion')}
                      className={cn("flex items-center justify-center gap-2 font-bold text-sm hover:opacity-80 mx-auto", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}
                    >
                      Completion
                      {sortBy === 'completion' && (sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
                    </button>
                  </div>
                  <div className="col-span-2 text-center">
                    <span className={cn("font-bold text-sm", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Core/Deep</span>
                  </div>
                </div>
                <div className="space-y-3">
                  {sortedStudents.map((student) => (
                    <div key={student.id} className={cn("border rounded-xl p-4 cursor-pointer hover:bg-opacity-50 transition-colors", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-white border-[#E0D4BF]")} onClick={() => handleViewStudentDetails(student.id)}>
                      {/* Desktop Grid View */}
                      <div className="hidden md:grid grid-cols-12 gap-4 items-center">
                        <div className="col-span-4">
                          <div className="flex items-center gap-4">
                            <div className={cn("w-12 h-12 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0", isDark ? "bg-[hsl(var(--gold-primary))] text-white" : "bg-[#C9A961] text-white")}>
                              {student.name.split(' ').map(n => n[0]).join('')}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-3 flex-wrap">
                                <h4 className={cn("font-bold text-base truncate", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{student.name}</h4>
                                <Badge className={cn("text-xs flex-shrink-0", getBadgeColor(isDark))}>
                                  {(student.usageLevel || 'low').toUpperCase()}
                                </Badge>
                              </div>
                              <p className={cn("text-sm font-medium mt-1 truncate", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>{getGradeAndDivision(student.class)}</p>
                            </div>
                          </div>
                        </div>
                        <div className="col-span-2 text-center">
                          <p className={cn("text-lg font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{student.totalInteractions || 0}</p>
                        </div>
                        <div className="col-span-2 text-center">
                          <p className={cn("text-lg font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{student.timeSpentMinutes || 0}m</p>
                        </div>
                        <div className="col-span-2 text-center">
                          <p className={cn("text-lg font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{student.completionRate ? student.completionRate.toFixed(1) : '0.0'}%</p>
                        </div>
                        <div className="col-span-2 text-center">
                          <p className={cn("text-lg font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{student.coreInteractions || 0}/{student.deepInteractions || 0}</p>
                        </div>
                      </div>
                      {/* Mobile View */}
                      <div className="md:hidden flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                          <div className={cn("w-12 h-12 rounded-full flex items-center justify-center font-bold text-sm", isDark ? "bg-[hsl(var(--gold-primary))] text-white" : "bg-[#C9A961] text-white")}>
                            {student.name.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <h4 className={cn("font-bold text-base", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{student.name}</h4>
                              <Badge className={cn("text-xs", getBadgeColor(isDark))}>
                                {(student.usageLevel || 'low').toUpperCase()}
                              </Badge>
                            </div>
                            <p className={cn("text-sm font-medium mt-1", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>{getGradeAndDivision(student.class)}</p>
                            <div className="flex items-center gap-4 mt-2">
                              <span className={cn("text-xs font-medium", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>{student.totalInteractions || 0} interactions</span>
                              <span className={cn("text-xs font-medium", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")}>{student.timeSpentMinutes || 0}m</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Simple Summary Card */}
        <Card className={cn("border shadow-sm", isDark ? "bg-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-white border-[#E0D4BF]")}>
          <CardHeader>
            <CardTitle className={cn("text-xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Quick Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={cn("p-4 rounded-lg border", isDark ? "bg-[hsl(var(--gold-primary))]/10 border-[hsl(var(--gold-primary))]/20" : "bg-[#C9A961]/10 border-[#C9A961]/20")}>
                <p className={cn("text-sm font-medium mb-1", isDark ? "text-[hsl(var(--gold-primary))]" : "text-[#C9A961]")}>Core Content Usage</p>
                <p className={cn("text-2xl font-bold", isDark ? "text-[hsl(var(--gold-primary))]" : "text-[#C9A961]")}>{coreVsDeepStats.totalCore} interactions</p>
              </div>
              <div className={cn("p-4 rounded-lg border", isDark ? "bg-[hsl(var(--gold-medium))]/10 border-[hsl(var(--gold-medium))]/20" : "bg-[#D4AF77]/10 border-[#D4AF77]/20")}>
                <p className={cn("text-sm font-medium mb-1", isDark ? "text-[hsl(var(--gold-medium))]" : "text-[#D4AF77]")}>Deep Content Usage</p>
                <p className={cn("text-2xl font-bold", isDark ? "text-[hsl(var(--gold-medium))]" : "text-[#D4AF77]")}>{coreVsDeepStats.totalDeep} interactions</p>
              </div>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}

